import { ItemRenderer, MultiSelect } from "@blueprintjs/select";
import React from "react";

import { Button, Classes, ITagProps, MenuItem, Spinner } from "@blueprintjs/core";
import { IViewProps, View } from "core/decorators/View";
import Class from "core/util/Class";
import ServiceRegistry from "core/util/ServiceRegistry";
import { IWidgetProps } from "decorator-form";
import { TagViewConfig } from "./TagViewConfig";


/**
 * TODO refactor this and entitySelect folder.
 *
 * need to have a better way to distinguish between context edit and panel widgets/fields.
 *
 * @author chills
 */

interface IProps<T, V> extends IViewProps<TagViewConfig>, IWidgetProps<V[]> {
	entity: Class<T>;
	getKey: (o: T) => React.Key;

	placeholder?: string;

	// customize "value" type
	getSearchParams?: object | ((query: string) => object); // default { query }
	getDefaultValueParams?: (value: V[]) => object; // default { ids: [...value] }
	getValue?: string | ((o: T) => V); // default o.id
	getLabel?: string | ((o: T) => string | JSX.Element); // default o.name
	equals?: (v1: V, v2: V) => boolean; // default o1.id === o2.id
	getRightElement?: string | ((o: T) => string | JSX.Element);
	onBeforeRenderItem?: (item: T, items: T[]) => JSX.Element | null; 
	queryOnStartup?: string;
	openOnKeyDown?: boolean;
	ariaLabel?: string;
}

interface IState<T> {
	query: string; // current query string (controlled)
	loading: boolean; // loading initial value
	errors: any[]; // TODO common interface for displaying errors?

	// objects retrieved based on current value (value might just be the ids)
	selected: T[];

	// current search results
	items: T[];
	loadingItems: boolean; // currently loading more items
}

@View({
	id: 'tag-multiselect',
	configClass: TagViewConfig
})
export default class EntityMultiTagInput<T, V = any> extends React.Component<IProps<T, V>, IState<T>> {

	private timer: any;

	public constructor(props: IProps<T, V>) {
		super(props);
		this.state = {
			query: "",
			loading: false,
			errors: [],
			selected: [],
			loadingItems: false,
			items: []
		};
	}

	public render() {
		const { config, placeholder, ariaLabel } = this.props;

		// selected should be based on this.props.value, not objects in state
		const selectedItems = this.props.value && this.props.value.length > 0 && this.props.value.reduce((a, c) => {

			const o = this.state.selected.find((s) => this.valueEquals(c, this.getValue(s)));

			if (o) {
				a.push(o);
			}

			return a;
		}, []) || [];

		const tagInputProps = {
			onRemove: this.onRemoveTag,
			tagProps: {
				round: config.round,
				large: config.large,
				minimal: config.minimal
			},
			inputProps:{
				"aria-label": ariaLabel
			},
			fill: true,
			placeholder,
			rightElement: (
				this.state.loading ? <Spinner size={Spinner.SIZE_SMALL} /> :
					(selectedItems.length > 0) ? <Button minimal={true} icon="cross" onClick={this.onClear} /> :
					undefined
			)
		};
		return (
			<MultiSelect
				items={this.state.items}
				itemsEqual={this.objectEquals}
				fill={true}
				query={this.state.query}
				tagInputProps={tagInputProps}
				onQueryChange={this.onChangeQuery}
				resetOnSelect={true}
				openOnKeyDown={this.props.openOnKeyDown === false ? false:true}
				tagRenderer={this.getLabel}
				selectedItems={selectedItems}
				itemRenderer={this.renderItem}
				onItemSelect={this.onItemSelect}
				noResults={
					this.state.loadingItems ? (
						<MenuItem
							className={Classes.SKELETON}
						/>
					) : (
						<MenuItem
							text="No Matches"
							disabled={true}
						/>
					)
				}
			/>
		);
	}

	public componentDidMount() {
		if (this.props.value && this.props.value.length > 0) {
			this.setState({loading: true}, () => {

				ServiceRegistry.getQueryService()
					.findMany(this.props.entity, this.getDefaultValueParams())
					.then((result: any) => {
						const selected: T[] = result.data && result.data.entities || [];
						this.setState({loading: false, selected});
					})
					.catch((reason) => {
						this.setState({
							loading: false,
							errors: ["Failed to load selected values"]
						});
					});
			});
		}
		if(this.props.queryOnStartup != undefined)
			this.loadData(this.props.queryOnStartup);
	}

	private renderItem: ItemRenderer<T> = (o: T, { handleClick, modifiers }) => {

		const isSelected = this.isSelected(o);

		return (
			<React.Fragment>
				
				{this.props.onBeforeRenderItem && this.props.onBeforeRenderItem(o,this.state.items)}
				<MenuItem
					key={this.getKey(o)}
					text={this.getLabel(o)}
					icon={isSelected ? 'tick' : 'blank'}
					labelElement={this.getRightElement(o)}
					onClick={handleClick}
					active={modifiers.active}
					shouldDismissPopover={false}
				/>
			</React.Fragment>
		);
	}

	private onClear = () => {
		this.setState({selected: []}, () => this.props.onChange([]));
	}

	private onRemoveTag = (_: any, index: number) => {
		const valueToRemove = this.getValue(this.state.selected[index]);

		this.setState({
			selected: this.state.selected.filter((_, i) => i !== index)
		}, () => {
			this.props.onChange((this.props.value || []).filter((v) => !this.valueEquals(v, valueToRemove)));
		});
	}

	private onItemSelect = (o: T) => {
		const val = this.getValue(o);
		const isSelected = this.isSelected(o);

		// toggle value
		if (isSelected) {
			this.setState({
				selected: (this.state.selected || []).filter((s) => {
					return !this.valueEquals(val, this.getValue(s));
				})
			}, () => {
				this.props.onChange((this.props.value || []).filter((v) => {
					return !this.valueEquals(v, val);
				}));
			});
		} else {
			this.setState({
				selected: [...this.state.selected || [], o]
			}, () => {
				this.props.onChange([...this.props.value || [], val]);
			});
		}
	}

	private onChangeQuery = (query: string) => {
		this.setState({query}, () => {
			if (!!this.timer) {
				clearTimeout(this.timer);
			}
			this.timer = setTimeout(() => this.loadData(query), 300);
		});
	}

	private loadData = (query: string) => {
		this.setState({loadingItems: true}, () => {

			ServiceRegistry.getQueryService()
				.findMany(this.props.entity, this.getSearchParams(query))
				.then((result: any) => {
					this.setState({
						errors: [],
						items: result.data && result.data.entities || [],
						loadingItems: false
					});
				})
				.catch(() => {
					this.setState({
						errors: ["Failed to load options"],
						loading: false
					});
				});
		});
	}

	private getDefaultValueParams = () => {
		if (typeof this.props.getDefaultValueParams === 'function') {
			return this.props.getDefaultValueParams(this.props.value);
		}
		return { ids: this.props.value };
	}

	private getSearchParams = (query: string): object => {
		switch(typeof this.props.getSearchParams) {
			case 'function':
				return (this.props.getSearchParams as any)(query);
			case 'object':
				return {...this.props.getSearchParams, query: query || null };
			default:
				return { query: this.state.query || null };
		}
	}

	private getLabel = (o: T): string | JSX.Element => {
		return typeof this.props.getLabel === 'function' ? this.props.getLabel(o) : o[this.props.getLabel || 'name'];
	}

	private getRightElement = (o: T): string | JSX.Element => {
		if (typeof this.props.getRightElement === 'function') {
			return this.props.getRightElement(o);
		}
		return !!this.props.getRightElement && o[this.props.getRightElement] || null;
	}

	private getValue = (o: T): V => {
		return typeof this.props.getValue === 'function' ? this.props.getValue(o) : o[this.props.getValue || "id"];
	}

	private getKey = (o: T) => typeof this.props.getKey === 'function' ? this.props.getKey(o) : o['id'];

	private objectEquals = (o1: T, o2: T) => {
		return this.valueEquals(this.getValue(o1), this.getValue(o2));
	}

	private valueEquals = (v1: V, v2: V): boolean => {
		return typeof this.props.equals === 'function' ? this.props.equals(v1, v2) : v1 === v2;
	}

	private isSelected = (o: T): boolean => {
		if (!this.props.value || this.props.value.length < 1) {
			return false;
		}
		const thisValue = this.getValue(o);
		for (const v of this.props.value) {
			if (this.valueEquals(v, thisValue)) {
				return true;
			}
		}
		return false;
	}
}



// WEBPACK FOOTER //
// ./src/common/views/tag/EntityMultiTagInput.view.tsx